import { quotationApi } from './axios';
import { useQuery } from '@tanstack/react-query';
import { ValidateCurrentToken, LogoutSystem } from '../../helpers';
import {
  ServiceResponseI,
  DataI,
  ListQuotationI,
  QuotationI,
  GetQuotationParamsI,
  NewQuotationI,
  UpdateQuotationI,
  ConvertQuotationI,
} from '@interfaces';
import { AxiosError } from 'axios';

// ─── GET ALL paginado ─────────────────────────────────────
const getQuotations = async ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  customerId,
  dateFrom,
  dateTo,
  tipo,
}: GetQuotationParamsI): Promise<ServiceResponseI<DataI<ListQuotationI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error('TOKEN INVALIDO');

    const params = new URLSearchParams();
    params.append('page',    (pagination.pageIndex + 1).toString());
    params.append('limit',   pagination.pageSize.toString());
    params.append('search',  globalFilter ?? '');
    params.append('sorting', JSON.stringify(sorting ?? []));
    if (branchId)   params.append('branchId',   branchId);
    if (customerId) params.append('customerId', customerId);
    if (dateFrom)   params.append('dateFrom',   dateFrom);
    if (dateTo)     params.append('dateTo',     dateTo);
    if (tipo)       params.append('tipo',       tipo);

    const { data } = await quotationApi.get<ServiceResponseI<DataI<ListQuotationI>>>('', {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// ─── GET BY ID ────────────────────────────────────────────
export const quotationById = async (
  id: string,
): Promise<ServiceResponseI<QuotationI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error('TOKEN INVALIDO');

    const { data } = await quotationApi.get<ServiceResponseI<QuotationI>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// ─── POST crear ───────────────────────────────────────────
export const quotationPost = async (
  payload: NewQuotationI,
): Promise<ServiceResponseI<{ id: string; numberQuotation: string }>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error('TOKEN INVALIDO');

    const { data } = await quotationApi.post<
      ServiceResponseI<{ id: string; numberQuotation: string }>
    >(
      '', payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// ─── PUT editar (solo BORRADOR) ────────────────────────────
export const quotationUpdate = async (
  id: string,
  payload: UpdateQuotationI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error('TOKEN INVALIDO');

    const { data } = await quotationApi.put<ServiceResponseI<string>>(
      `/${id}`, payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// ─── PATCH genérico de estado ─────────────────────────────
const patchQuotationStatus = async (
  endpoint: string,
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error('TOKEN INVALIDO');

    const { data } = await quotationApi.patch<ServiceResponseI<string>>(
      `/${endpoint}/${id}`,
      {},
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const quotationSend   = (id: string) => patchQuotationStatus('send',     id);
export const quotationAccept = (id: string) => patchQuotationStatus('accept',   id);
export const quotationReject = (id: string) => patchQuotationStatus('reject',   id);
export const quotationExpire = (id: string) => patchQuotationStatus('expire',   id);
export const quotationAnnul  = (id: string) => patchQuotationStatus('annullar', id);

// ─── POST convertir a venta ───────────────────────────────
export const quotationConvert = async (
  id: string,
  payload: ConvertQuotationI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error('TOKEN INVALIDO');

    const { data } = await quotationApi.post<ServiceResponseI<string>>(
      `/convert/${id}`, payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// ─── HOOK principal (React Query) ─────────────────────────
export const useQuotations = ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  customerId,
  dateFrom,
  dateTo,
  tipo,
}: GetQuotationParamsI) => {
  const quotationsQuery = useQuery(
    ['quotations', { pagination, sorting, globalFilter, branchId, customerId, dateFrom, dateTo, tipo }],
    () => getQuotations({ pagination, sorting, globalFilter, branchId, customerId, dateFrom, dateTo, tipo }),
    { refetchOnWindowFocus: false, keepPreviousData: true },
  );
  return { quotationsQuery };
};